import React, { useCallback, useMemo } from "react"
import { Alert, Box, Card, CardContent, Chip, Fab, Typography, useMediaQuery } from "@mui/material"
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard"
import { SpinWheelCanvas } from "react-ui-components/components/SpinWheel/SpinWheelCanvas"
import { SpinWheelPrizePool } from "react-ui-components/components/SpinWheel/SpinWheelPrizePool"
import { useSpinWheelSpin } from "react-ui-components/components/SpinWheel/useSpinWheelSpin"

import { PresentationFormData, WheelPrizeFormData, WinnerNotificationsFormData } from "./wheelPrizeTypes"
import { calculateOddsPercent } from "./wheelPrizeValidation"
import { buildWinnerNotificationPreview } from "./wheelWinnerNotificationLogic"

const describePrize = (prize: WheelPrizeFormData): string => {
  switch (prize.type) {
    case "facilCoins":
      return `${prize.amount ?? 0} Facil-Coins`
    case "raffleEntry":
      return `${prize.ticketsCount ?? 1} raffle entry(ies)`
    case "cash":
      return `${prize.currency ?? "USD"} ${prize.amount ?? 0}`
    case "bonusSpin":
      return `+${prize.spinsAwarded ?? 1} spin(s)`
    case "product":
      return prize.serviceTitle || "Product"
    case "coupon":
      return prize.offerTitle || "Coupon"
    default:
      return prize.description ?? ""
  }
}

// Picks a winner the same way the backend does (weighted by `weight` among currently enabled
// prizes) so "Test Spin" gives admins a realistic feel for the configured odds. Unlike the
// backend, it doesn't account for inventory caps or date windows — it's just previewing what's
// on screen right now.
const pickWeightedPrize = (prizes: WheelPrizeFormData[]): WheelPrizeFormData | null => {
  const weighted = prizes.filter((prize) => prize.weight > 0)
  const totalWeight = weighted.reduce((sum, prize) => sum + prize.weight, 0)
  if (totalWeight <= 0) {
    return null
  }
  let roll = Math.random() * totalWeight
  for (const prize of weighted) {
    roll -= prize.weight
    if (roll <= 0) {
      return prize
    }
  }
  return weighted[weighted.length - 1] ?? null
}

// Shares its wheel rendering, spin animation, and prize-pool layout with the production daily
// spin wheel via react-ui-components/components/SpinWheel — this is a real, spinnable wheel
// (not just a static approximation), it just resolves the winner locally from the currently
// configured weights instead of calling the backend, so admins can test odds/config before
// publishing.
export const WheelLivePreview: React.FC<{
  presentation: PresentationFormData
  prizes: WheelPrizeFormData[]
  winnerNotifications: WinnerNotificationsFormData
}> = ({ presentation, prizes, winnerNotifications }) => {
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)")
  const enabledPrizes = useMemo(() => prizes.filter((prize) => prize.enabled), [prizes])
  const odds = calculateOddsPercent(prizes)
  const prizeIds = useMemo(() => enabledPrizes.map((prize) => prize.id), [enabledPrizes])

  const resolveWinner = useCallback(async () => {
    const prize = pickWeightedPrize(enabledPrizes)
    return { winningPrizeId: prize?.id ?? null, result: prize }
  }, [enabledPrizes])

  const { phase, result, landed, isBusy, rotation, spin } = useSpinWheelSpin<WheelPrizeFormData | null>({
    prizeIds,
    prefersReducedMotion,
    resolveWinner,
  })

  const notificationPreview = result ? buildWinnerNotificationPreview(result, winnerNotifications) : null

  return (
    <Card variant="outlined" sx={{ bgcolor: "background.default" }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Live preview — click SPIN to test the configured odds and prize art.
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
            p: 1.25,
            borderRadius: 2,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Fab
            size="large"
            color={presentation.fabBackgroundColor ? undefined : "secondary"}
            aria-hidden
            sx={{
              pointerEvents: "none",
              flexShrink: 0,
              ...(presentation.fabBackgroundColor && {
                bgcolor: presentation.fabBackgroundColor,
                color: (theme) => theme.palette.getContrastText(presentation.fabBackgroundColor as string),
                "&:hover": { bgcolor: presentation.fabBackgroundColor },
              }),
            }}
          >
            {presentation.fabIconUrl ? (
              <Box
                component="img"
                src={presentation.fabIconUrl}
                alt=""
                sx={{ width: "60%", height: "60%", objectFit: "contain" }}
              />
            ) : (
              <CardGiftcardIcon />
            )}
          </Fab>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600}>
              Home screen launcher button
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Floats on the Home screen and opens this wheel. Falls back to the gift icon when no
              launcher icon is uploaded.
            </Typography>
          </Box>
        </Box>

        {presentation.bannerImageUrl && (
          <Box
            component="img"
            src={presentation.bannerImageUrl}
            alt=""
            sx={{ width: "100%", borderRadius: 1, mb: 2, maxHeight: 100, objectFit: "cover" }}
          />
        )}

        <Typography variant="h6" sx={{ color: presentation.accentColor }}>
          {presentation.title || "La Ruleta Win"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {presentation.subtitle}
        </Typography>

        <SpinWheelCanvas
          prizes={enabledPrizes}
          rotation={rotation}
          size={presentation.wheelSize === "large" ? 220 : presentation.wheelSize === "compact" ? 140 : 180}
          accentColor={presentation.accentColor}
          spinning={isBusy}
          disabled={isBusy || enabledPrizes.length === 0}
          onSpin={() => void spin()}
          spinLabel={isBusy ? "..." : presentation.spinButtonText || "SPIN"}
          landed={landed}
          spinButtonPlacement={presentation.spinButtonPlacement}
        />

        {phase === "landed" && (
          <Box sx={{ textAlign: "center", mt: 1 }}>
            <Chip
              color={result ? (result.winning ? "success" : "default") : "default"}
              label={
                result
                  ? result.winning
                    ? `Test result: ${result.label}`
                    : `Test result: ${result.label} (Try Again — no win)`
                  : "No eligible prize (check weights)"
              }
            />
          </Box>
        )}

        {phase === "landed" && result && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Winner notification preview
            </Typography>
            {notificationPreview ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  p: 1.25,
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: 2,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    flexShrink: 0,
                    borderRadius: "22.5%",
                    bgcolor: presentation.accentColor,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 16,
                  }}
                >
                  🎡
                </Box>
                <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                    <Typography variant="body2" fontWeight={700} noWrap>
                      {notificationPreview.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      now
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {notificationPreview.body}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Alert severity="info" sx={{ textAlign: "left" }}>
                No winner announcement would be sent for this prize — check the Winner tab settings.
              </Alert>
            )}
          </Box>
        )}

        {presentation.showPrizePool && (
          <Box sx={{ mt: 2 }}>
            <SpinWheelPrizePool prizes={enabledPrizes} showOdds={presentation.showOdds} odds={odds} describePrize={describePrize} />
          </Box>
        )}

        {!presentation.showOdds && (
          <Typography variant="caption" color="text.secondary" display="block" align="center" sx={{ mt: 1 }}>
            Prize probabilities may vary.
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

export default WheelLivePreview
